import config from '@/config';
import { cleanUrl, getCommunityUrl, getK12ZoneUrl } from '@/utils/linksUtils';
import { applyTasklistPlaceholders } from '@/utils/tasklistPagePlaceholders';
import {
  parseTasklistDynamicLinkKey,
  TasklistDynamicLinkKey,
  type TasklistPageLinkMeta,
} from '@/types/tasklistDynamicLinks';

export type { TasklistDynamicLinkKey, TasklistDynamicLinkType, TasklistPageLinkMeta } from '@/types/tasklistDynamicLinks';
export { isTasklistDynamicLinkKey, parseTasklistDynamicLinkKey, TasklistDynamicLinkKey, TasklistDynamicLinkType } from '@/types/tasklistDynamicLinks';

export interface TasklistLinkSources {
  /** Raw school-link API URL (Canvas / Launch Pad). */
  schoolLinkUrl: string | null;
  /** Resolved primary K12 School CTA href (LP/OLS matrix). */
  k12SchoolHref: string | null;
  myAccountLink: string | null;
  k12ZoneLink: string | null;
  calendarLink: string | null;
  enrollmentPortal: string | null;
  communityUrl: string | null;
}

const DYNAMIC_LINK_PATTERN = /\{\{(\w+)\}\}/g;

export function buildTasklistLinkSources(args: {
  schoolLinkUrl: string | null;
  k12SchoolHref: string | null;
  myAccountLink: string | null;
  k12ZoneLink: string | null;
  calendarLink: string | null;
  userRoleVal?: string;
}): TasklistLinkSources {
  return {
    schoolLinkUrl: args.schoolLinkUrl,
    k12SchoolHref: args.k12SchoolHref,
    myAccountLink: args.myAccountLink,
    k12ZoneLink: getK12ZoneUrl(args.userRoleVal, args.k12ZoneLink ?? undefined) ?? null,
    calendarLink: cleanUrl(args.calendarLink) ?? null,
    enrollmentPortal: cleanUrl(config.LINKS.ENROLLMENT_PORTAL) ?? null,
    communityUrl: getCommunityUrl() ?? null,
  };
}

export function resolveTasklistLinkKey(
  key: string,
  sources: TasklistLinkSources
): string | null {
  const parsed = parseTasklistDynamicLinkKey(key);
  if (!parsed) return null;

  switch (parsed) {
    case TasklistDynamicLinkKey.CanvasLink:
      return cleanUrl(sources.k12SchoolHref) ?? cleanUrl(sources.schoolLinkUrl) ?? null;
    case TasklistDynamicLinkKey.MyAccountLink:
      return cleanUrl(sources.myAccountLink) ?? null;
    case TasklistDynamicLinkKey.K12ZoneLink:
      return cleanUrl(sources.k12ZoneLink) ?? null;
    case TasklistDynamicLinkKey.CalendarLink:
      return cleanUrl(sources.calendarLink) ?? null;
    case TasklistDynamicLinkKey.EnrollmentPortal:
      return cleanUrl(sources.enrollmentPortal) ?? null;
    case TasklistDynamicLinkKey.CommunityUrl:
      return cleanUrl(sources.communityUrl) ?? null;
    default:
      return null;
  }
}

/** Substitutes markdown link targets like `[My Account]({{myAccountLink}})`. */
export function applyTasklistDynamicLinks(text: string, sources: TasklistLinkSources): string {
  return text.replace(DYNAMIC_LINK_PATTERN, (_, key: string) => resolveTasklistLinkKey(key, sources) ?? '');
}

export function applyTasklistPageContent(
  text: string,
  vars: { studentName?: string; school?: string; schoolAbbr?: string },
  sources: TasklistLinkSources
): string {
  return applyTasklistDynamicLinks(applyTasklistPlaceholders(text, vars), sources);
}

/**
 * Resolves structured button / CTA `buttonUrl` values.
 * Static URLs pass through; link keys resolve via {@link resolveTasklistLinkKey}.
 */
export function resolveTasklistButtonHref(
  buttonUrl: string,
  linkMeta: TasklistPageLinkMeta | undefined,
  sources: TasklistLinkSources
): string | null {
  const key =
    (linkMeta?.linkKey && parseTasklistDynamicLinkKey(String(linkMeta.linkKey))) ||
    parseTasklistDynamicLinkKey(buttonUrl.trim());

  if (key) {
    return resolveTasklistLinkKey(key, sources);
  }

  const trimmed = buttonUrl.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/') || /^https?:\/\//i.test(trimmed) || trimmed.startsWith('mailto:')) {
    return trimmed;
  }
  if (trimmed.includes('.')) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

export function isTasklistExternalHref(href: string): boolean {
  const t = href.trim();
  return /^https?:\/\//i.test(t) || t.startsWith('mailto:');
}
